/**
 * IndexedDB sync queue for offline-first writes.
 * Queues PATCH/POST mutations when offline; flushes them when reconnected.
 *
 * Operations supported:
 *   - { type: 'create_finding', engagement_id, payload, tempId }
 *   - { type: 'update_finding', finding_id, payload }
 *   - { type: 'delete_finding', finding_id }
 *   - { type: 'update_gap_item', item_id, payload }
 *   - { type: 'update_engagement', engagement_id, payload }
 */
import { openDB } from 'idb';
import { api } from './api';

const DB_NAME = 'gigline-offline';
const DB_VERSION = 1;
const STORE_QUEUE = 'queue';
const STORE_CACHE = 'cache';

async function getDB() {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_QUEUE)) {
        db.createObjectStore(STORE_QUEUE, { keyPath: 'id', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(STORE_CACHE)) {
        db.createObjectStore(STORE_CACHE, { keyPath: 'key' });
      }
    },
  });
}

export async function enqueue(op) {
  const db = await getDB();
  const id = await db.add(STORE_QUEUE, { ...op, queued_at: Date.now() });
  return id;
}

export async function listQueue() {
  const db = await getDB();
  return db.getAll(STORE_QUEUE);
}

export async function clearQueueItem(id) {
  const db = await getDB();
  return db.delete(STORE_QUEUE, id);
}

export async function queueSize() {
  const db = await getDB();
  return db.count(STORE_QUEUE);
}

export async function cachePut(key, value) {
  const db = await getDB();
  return db.put(STORE_CACHE, { key, value, updated_at: Date.now() });
}

export async function cacheGet(key) {
  const db = await getDB();
  const row = await db.get(STORE_CACHE, key);
  return row?.value ?? null;
}

let _flushing = false;
const listeners = new Set();
export function onSyncEvent(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
function emit(evt) {
  listeners.forEach((cb) => {
    try {
      cb(evt);
    } catch (_e) {
      // ignore listener errors
    }
  });
}

export async function flushQueue() {
  if (_flushing) return { skipped: true };
  _flushing = true;
  emit({ type: 'start' });
  try {
    const items = await listQueue();
    let ok = 0;
    let fail = 0;
    for (const op of items) {
      try {
        await applyOp(op);
        await clearQueueItem(op.id);
        ok++;
        emit({ type: 'progress', done: ok, total: items.length });
      } catch (e) {
        fail++;
      }
    }
    emit({ type: 'done', ok, fail });
    return { ok, fail };
  } finally {
    _flushing = false;
  }
}

async function applyOp(op) {
  switch (op.type) {
    case 'create_finding':
      return api.post(`/engagements/${op.engagement_id}/findings`, op.payload);
    case 'update_finding':
      return api.patch(`/findings/${op.finding_id}`, op.payload);
    case 'delete_finding':
      return api.delete(`/findings/${op.finding_id}`);
    case 'update_gap_item':
      return api.patch(`/gap_items/${op.item_id}`, op.payload);
    case 'update_engagement':
      return api.patch(`/engagements/${op.engagement_id}`, op.payload);
    default:
      throw new Error(`Unknown op ${op.type}`);
  }
}

// Auto-flush when coming back online
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    flushQueue();
  });
}
